<template>
  <v-col>
    <bread-crumbs />

    <v-row
      v-if="showSummaries"
      class="ma-0"
    >
      <v-col>
        <v-row class="summary-bar px-2">
          <v-col
            v-for="summary in Object.values(localDiscrepancySummaries)"
            :key="summary.id"
            class="col-auto"
          >
            <v-card
              :class="`pa-3 ma-2 ${summary.styles.background} ${summary.styles.text}--text text-center`"
              min-width="180"
            >
              <v-card-title
                class="pa-0 subtitle-2 font-weight-bold"
              >
                <v-col>{{ summary.title }}</v-col>
              </v-card-title>

              <v-card-subtitle
                v-if="summary.subtitle"
                :class="`pa-0 caption font-weight-medium ${summary.styles.text}--text`"
              >
                <v-col class="pt-1">
                  {{ summary.subtitle }}
                </v-col>
              </v-card-subtitle>

              <v-card-text
                class="pa-0 mb-2 headline font-weight-bold text-center"
                :style="`color: ${summary.styles.value}`"
              >
                {{ numberFormatter(summary.total) }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row class="ma-0">
      <v-col>
        <report-list v-if="showDashboard" />

        <v-row v-else>
          <data-table
            ref="table"
            :actions="actions"
          >
            <template v-slot:empty-state>
              <div>Selected publisher has no Discrepancy data.</div>
            </template>
          </data-table>

          <creatives-discrepancy-modal />
        </v-row>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import format from 'src/utils/format'

const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'Discrepancy',
  components: {
    BreadCrumbs: () => import('@/layout/components/BreadCrumbs'),
    CreativesDiscrepancyModal: () => import('../components/modals/CreativesDiscrepancyModal'),
    DataTable: () => import('@/tools/components/table/DataTable'),
    ReportList: () => import('@/dashboard/components/ReportList'),
  },
  data() {
    return {
      actions: [
        {
          event: 'delivery/openTabToLineItem',
          name: 'Go to Line Item',
        },
        {
          event: 'delivery/openTabToOrder',
          name: 'Go to Order',
        },
        {
          event: 'delivery/showCreativesDiscrepancyModal',
          name: 'Creatives Discrepancy',
        },
      ],
    }
  },
  computed: {
    ...mapToolsGetters([
      'showSummaries',
      'currentGroups',
    ]),
    ...mapToolsState({
      selectedSearch: (state) => state.selectedSearch,
    }),
    showDashboard() {
      return this.selectedSearch?.dataType === 'custom'
    },

    // Local summary calculations based on current filtered discrepancy data
    localDiscrepancySummaries() {
      const currentData = (this.currentGroups || []).flatMap((group) => group.items || [])

      // Calculate counts for each discrepancy range based on the line item data
      const zeroDiscrepancyCount = this.calculateZeroDiscrepancyCount(currentData)
      const lowPositiveCount = this.calculateLowPositiveDiscrepancyCount(currentData)
      const lowNegativeCount = this.calculateLowNegativeDiscrepancyCount(currentData)
      const highPositiveCount = this.calculateHighPositiveDiscrepancyCount(currentData)
      const highNegativeCount = this.calculateHighNegativeDiscrepancyCount(currentData)

      // Return summary objects matching the expected format
      return {
        DISCREPANCY_0: {
          id: 'DISCREPANCY_0',
          title: '0% Discrepancy',
          subtitle: this.getDiscrepancySubtitle(),
          total: zeroDiscrepancyCount,
          format: 'number',
          styles: {
            background: 'success',
            text: 'white',
            value: 'white',
          },
        },
        DISCREPANCY_BETWEEN_1_10: {
          id: 'DISCREPANCY_BETWEEN_1_10',
          title: '1-10% Positive',
          subtitle: this.getDiscrepancySubtitle(),
          total: lowPositiveCount,
          format: 'number',
          styles: {
            background: 'warning',
            text: 'white',
            value: 'white',
          },
        },
        DISCREPANCY_BETWEEN_MINUS_1_10: {
          id: 'DISCREPANCY_BETWEEN_MINUS_1_10',
          title: '1-10% Negative',
          subtitle: this.getDiscrepancySubtitle(),
          total: lowNegativeCount,
          format: 'number',
          styles: {
            background: 'warning',
            text: 'white',
            value: 'white',
          },
        },
        DISCREPANCY_BEYOND_10: {
          id: 'DISCREPANCY_BEYOND_10',
          title: '>10% Positive',
          subtitle: this.getDiscrepancySubtitle(),
          total: highPositiveCount,
          format: 'number',
          styles: {
            background: 'error',
            text: 'white',
            value: 'white',
          },
        },
        DISCREPANCY_BEYOND_MINUS_10: {
          id: 'DISCREPANCY_BEYOND_MINUS_10',
          title: '>10% Negative',
          subtitle: this.getDiscrepancySubtitle(),
          total: highNegativeCount,
          format: 'number',
          styles: {
            background: 'error',
            text: 'white',
            value: 'white',
          },
        },
      }
    },
  },
  methods: {
    // Local discrepancy calculation methods
    calculateZeroDiscrepancyCount(data) {
      // Count line items with discrepancy between -1% and 1%
      return data.filter((item) => {
        const discrepancy = item.discrepancyPercentage || 0
        return discrepancy >= -1 && discrepancy < 1
      }).length
    },

    calculateLowPositiveDiscrepancyCount(data) {
      // Count line items with discrepancy between 1% and 10%
      return data.filter((item) => {
        const discrepancy = item.discrepancyPercentage || 0
        return discrepancy >= 1 && discrepancy <= 10
      }).length
    },

    calculateLowNegativeDiscrepancyCount(data) {
      // Count line items with discrepancy between -10% and -1%
      return data.filter((item) => {
        const discrepancy = item.discrepancyPercentage || 0
        return discrepancy >= -10 && discrepancy <= -1
      }).length
    },

    calculateHighPositiveDiscrepancyCount(data) {
      // Count line items with discrepancy greater than 10%
      return data.filter((item) => {
        const discrepancy = item.discrepancyPercentage || 0
        return discrepancy >= 10
      }).length
    },

    calculateHighNegativeDiscrepancyCount(data) {
      // Count line items with discrepancy less than -10%
      return data.filter((item) => {
        const discrepancy = item.discrepancyPercentage || 0
        return discrepancy <= -10
      }).length
    },

    getDiscrepancySubtitle() {
      // Create contextual subtitle based on current filters/date range
      const { selectedSearch } = this
      if (selectedSearch && selectedSearch.reportName) {
        return selectedSearch.reportName
      }
      return 'Current Period'
    },

    numberFormatter(value) {
      return format.number(value)
    },

    /**
     * Clean URL query parameters by keeping only recognized parameters
     * @param {Object} currentQuery - Current route query object
     * @returns {Object} Cleaned query object with only valid parameters
     */
    cleanQueryParameters(currentQuery) {
      // Define the whitelist of recognized query parameters for the Discrepancy component
      const validParameters = ['publisher', 'team', 'startDate', 'endDate', 'lineItem', 'order', 'discrepancy']

      const cleanedQuery = {}

      // Only keep parameters that are in our whitelist
      validParameters.forEach((param) => {
        if (currentQuery[param] !== undefined) {
          cleanedQuery[param] = currentQuery[param]
        }
      })

      return cleanedQuery
    },

    /**
     * Update URL with clean parameters for bookmarkable states
     * @param {Object} newParams - New parameters to add/update
     */
    updateCleanUrl(newParams = {}) {
      const currentQuery = this.$route.query || {}
      const cleanedQuery = this.cleanQueryParameters(currentQuery)

      // Merge in new parameters
      Object.assign(cleanedQuery, newParams)

      // Only update route if query actually changed
      const currentQueryString = JSON.stringify(currentQuery)
      const cleanedQueryString = JSON.stringify(cleanedQuery)

      if (currentQueryString !== cleanedQueryString) {
        this.$router.push({ query: cleanedQuery })
      }
    },
  },
}
</script>
